import React, {useEffect, useMemo, useState} from "react"
import {Col, Divider, Row} from "antd"
import {useTranslation} from "react-i18next"
import {getFullName, handleError, toastSuccess} from "helpers"
import {BaseButton, BasePopup, SecondaryButton} from "components"
import {KlassDropAsyncPaginate} from "components/Select"
import {profileService, studentLedgerService} from "services"
import {StudentTransaction, StudentTransactionType} from "types/student-account/student-ledger"
import {formatCurrency} from "helpers"
import {Auth} from "types/auth"

type Props = {
    isShow: boolean
    onClose: () => void
    onSuccess: () => void
    sourceProfileId: number
    sourceStudent?: any
    transactions: StudentTransaction[]
}

const MoveTransactionsPopup = ({isShow, onClose, onSuccess, sourceProfileId, sourceStudent, transactions}: Props) => {
    const {t} = useTranslation(["studentAccount", "common"])
    const [targetProfile, setTargetProfile] = useState<any>(null)
    const [isMoving, setMoving] = useState(false)

    useEffect(() => {
        setTargetProfile(null)
    }, [isShow, sourceProfileId])

    const loadProfiles = async (search: string, _loadedOptions: any[], additional?: {offset: number}) => {
        try {
            const result = await profileService.getAll({
                filter: {search, type: Auth.UserProfileType.Student},
                range: {limit: 20, offset: additional?.offset ?? 0}
            })
            const profiles = result?.data ?? []
            return {
                options: profiles
                    .filter((profile) => profile.id !== sourceProfileId)
                    .map((profile) => ({
                        ...profile,
                        displayName: `${getFullName(profile)} (ID: ${profile.customProfileId ?? profile.id})`
                    })),
                hasMore: (additional?.offset ?? 0) + profiles.length < (result?.total ?? 0),
                additional: {offset: (additional?.offset ?? 0) + profiles.length}
            }
        } catch (error) {
            handleError(error)
            return {options: [], hasMore: false}
        }
    }

    const sourceLabel = useMemo(
        () =>
            `${getFullName(sourceStudent) || "Current student"} (ID: ${
                sourceStudent?.customProfileId ?? sourceProfileId
            })`,
        [sourceProfileId, sourceStudent]
    )

    const close = () => {
        setTargetProfile(null)
        onClose()
    }

    const move = async () => {
        if (!targetProfile || !transactions.length) return
        setMoving(true)
        try {
            await studentLedgerService.moveTransactions({
                sourceProfileId,
                targetProfileId: targetProfile.id,
                transactionIds: transactions.map((transaction) => transaction.tranxId)
            })
            toastSuccess(t("ledger.move.success"))
            close()
            onSuccess()
        } catch (error) {
            handleError(error)
        } finally {
            setMoving(false)
        }
    }

    return (
        <BasePopup isShow={isShow} onClose={close} width="760px" leftIcon="EDIT" leftIconColor="#fff">
            <div className="p-24">
                <h2>{t("ledger.move.title")}</h2>
                <Divider />
                <div className="mb-16">{t("ledger.move.targetStudent")}</div>
                <KlassDropAsyncPaginate
                    loadOptions={loadProfiles}
                    value={targetProfile}
                    valueKey="id"
                    labelKey="displayName"
                    isMulti={false}
                    isClearable
                    placeholder={t("ledger.move.selectTarget")}
                    onChange={setTargetProfile}
                />
                <Divider />
                <div className="mb-8">
                    <strong>{t("ledger.move.source")}: </strong>
                    {sourceLabel}
                </div>
                <div className="mb-16">
                    <strong>{t("ledger.move.target")}: </strong>
                    {targetProfile?.displayName || t("ledger.move.notSelected")}
                </div>
                <div>
                    <strong>{t("ledger.move.transactions")}</strong>
                    {transactions.map((transaction) => (
                        <Row key={transaction.tranxId} className="mt-8">
                            <Col span={7}>#{transaction.tranxId}</Col>
                            <Col span={7}>
                                {transaction.type === StudentTransactionType.Payment
                                    ? t("ledger.move.payment")
                                    : t("ledger.move.charge")}
                            </Col>
                            <Col span={10}>{formatCurrency(transaction.amount)}</Col>
                        </Row>
                    ))}
                </div>
                <Row justify="end" gutter={12} className="mt-24">
                    <Col>
                        <SecondaryButton title={t("common:action.cancel")} onClick={close} loading={isMoving} />
                    </Col>
                    <Col>
                        <BaseButton
                            title={t("common:action.confirm")}
                            onClick={move}
                            loading={isMoving}
                            disabled={!targetProfile || !transactions.length}
                        />
                    </Col>
                </Row>
            </div>
        </BasePopup>
    )
}

export default MoveTransactionsPopup
